import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Rocket,
  CheckCircle,
  XCircle,
  Lock,
  Shield,
  AlertTriangle,
  Clock,
  ExternalLink,
   Copy,
   Check,
   Link2,
   Mail,
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
 import { Input } from '@/components/ui/input';
 import { Label } from '@/components/ui/label';

interface BotPublishingProps {
  botId: string;
  tenantId: string;
  botStatus: string | null;
  botEnvironment: string | null;
  governancePackId: string | null;
  onStatusChange: (newStatus: string) => void;
}

interface ApprovalStatus {
  id: string;
  status: string;
  feedback_text: string | null;
  created_at: string;
}

const BotPublishing: React.FC<BotPublishingProps> = ({
  botId,
  tenantId,
  botStatus,
  botEnvironment,
  governancePackId,
  onStatusChange,
}) => {
  const [approval, setApproval] = useState<ApprovalStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [governanceVersion, setGovernanceVersion] = useState<number | null>(null);
  const [latestQARun, setLatestQARun] = useState<{ id: string; passed: boolean } | null>(null);
   const [linkCopied, setLinkCopied] = useState(false);
   const [generatingLink, setGeneratingLink] = useState(false);
   const [magicLink, setMagicLink] = useState<{ url: string; expiresAt: string } | null>(null);
   const [recipientEmail, setRecipientEmail] = useState('');
 
   const previewUrl = `${window.location.origin}/preview/${botId}`;
 
   const handleCopyLink = async (url: string) => {
     try {
       await navigator.clipboard.writeText(url);
       setLinkCopied(true);
       setTimeout(() => setLinkCopied(false), 2000);
       toast({ title: 'Link copied', description: 'Preview link copied to clipboard' });
     } catch (error) {
       console.error('Failed to copy:', error);
       toast({ title: 'Copy failed', description: 'Please copy the link manually', variant: 'destructive' });
     }
   };
 
   const generateMagicLink = async () => {
     setGeneratingLink(true);
     try {
       // Generate a secure token
       const token = crypto.randomUUID() + '-' + Date.now().toString(36);
       const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000); // 7 days
 
       const { error } = await supabase
         .from('bot_preview_links')
         .insert({
           bot_id: botId,
           token,
           recipient_email: recipientEmail || null,
           expires_at: expiresAt.toISOString(),
         });
 
       if (error) throw error;
 
       const magicUrl = `${window.location.origin}/preview/${botId}?token=${token}`;
       setMagicLink({ url: magicUrl, expiresAt: expiresAt.toISOString() });
 
       toast({ title: 'Magic link generated', description: 'Share this link with your client' });
     } catch (error) {
       console.error('Error generating magic link:', error);
       toast({ 
         title: 'Failed to generate link', 
         description: 'Please try again', 
         variant: 'destructive' 
       });
     } finally {
       setGeneratingLink(false);
     }
   };

  useEffect(() => {
    fetchApprovalAndGovernance();
  }, [botId, governancePackId]);

  const fetchApprovalAndGovernance = async () => {
    try {
      // Fetch approval status
      const { data: approvalData } = await supabase
        .from('approvals')
        .select('*')
        .eq('bot_id', botId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (approvalData) {
        setApproval(approvalData);
      }

      // Fetch governance pack version
      if (governancePackId) {
        const { data: govData } = await supabase
          .from('governance_packs')
          .select('version')
          .eq('id', governancePackId)
          .single();

        if (govData) {
          setGovernanceVersion(govData.version);
        }
      }

      // Fetch latest QA run
      const { data: qaData } = await supabase
        .from('qa_runs')
        .select('id, passed')
        .eq('bot_id', botId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (qaData) {
        setLatestQARun({ id: qaData.id, passed: qaData.passed ?? false });
      }
    } catch (error) {
      // Approval may not exist yet, which is fine
    } finally {
      setLoading(false);
    }
  };

  const canPublish = botStatus === 'approved' && botEnvironment === 'preview';
  const isLive = botStatus === 'live' && botEnvironment === 'production';
  const needsQA = botStatus === 'draft' || botStatus === 'qa_failed';
  const needsApproval = botStatus === 'ready_for_review';
  const clientApproved = approval?.status === 'approved';

  const handlePublish = async () => {
    setPublishing(true);

    try {
      // Update bot to production environment and lock governance
      const { error } = await supabase
        .from('bots')
        .update({
          environment: 'production',
          status: 'live',
          // governance_pack_id is already set - this locks it for the published version
        })
        .eq('id', botId);

      if (error) throw error;

      onStatusChange('live');

      toast({
        title: 'Bot Published!',
        description: 'Bot is now live in production. Governance pack version locked.',
      });

      setShowPublishDialog(false);
    } catch (error) {
      console.error('Error publishing bot:', error);
      toast({
        title: 'Publishing failed',
        description: 'Please try again or contact support.',
        variant: 'destructive',
      });
    } finally {
      setPublishing(false);
    }
  };

  // Check if QA actually passed (based on latest run, not just status)
  const qaActuallyPassed = latestQARun?.passed === true;

  // Checklist items for publishing
  const checklist = [
    {
      id: 'qa',
      label: 'QA tests passed',
      passed: qaActuallyPassed && botStatus !== 'draft' && botStatus !== 'qa_failed',
      required: true,
      failLink: latestQARun && !latestQARun.passed 
        ? `/admin/bots/${botId}?tab=qa&run=${latestQARun.id}` 
        : undefined,
    },
    {
      id: 'client-review',
      label: 'Client review completed',
      passed: botStatus === 'approved' || botStatus === 'live',
      required: true,
    },
    {
      id: 'governance',
      label: 'Governance pack attached',
      passed: !!governancePackId,
      required: true,
    },
  ];

  const allChecksPassed = checklist.filter(c => c.required).every(c => c.passed);

  return (
    <div className="space-y-6">
      {/* Publishing Status Card */}
      <Card className={isLive ? 'border-green-500/50' : ''}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Rocket className="w-5 h-5" />
            Publishing
            {isLive && (
              <Badge className="bg-green-500/10 text-green-600 ml-2">
                <CheckCircle className="w-3 h-3 mr-1" />
                Live
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            {isLive 
              ? 'Bot is live and serving visitors in production'
              : 'Publish this bot to make it available in production'
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Checklist */}
          <div className="space-y-3">
            <p className="text-sm font-medium">Pre-publish Checklist</p>
            {checklist.map(item => (
              <div key={item.id} className="flex items-center gap-3">
                {item.passed ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <XCircle className="w-5 h-5 text-muted-foreground" />
                )}
                <span className={item.passed ? 'text-foreground' : 'text-muted-foreground'}>
                  {item.label}
                </span>
                {item.required && !item.passed && (
                  <Badge variant="outline" className="text-xs">Required</Badge>
                )}
                {item.failLink && !item.passed && (
                  <Link to={item.failLink}>
                    <Button variant="ghost" size="sm" className="text-xs h-auto p-0 text-destructive">
                      View failing run
                      <ExternalLink className="w-3 h-3 ml-1" />
                    </Button>
                  </Link>
                )}
              </div>
            ))}
          </div>

          <Separator />

          {/* Governance Lock Info */}
          <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
            <Lock className="w-5 h-5 text-muted-foreground mt-0.5" />
            <div>
              <p className="font-medium text-sm">Governance Version Lock</p>
              <p className="text-xs text-muted-foreground">
                Publishing will lock governance pack {governancePackId ? `v${governanceVersion || 1}` : '(none)'}.
                Future governance updates won't affect this bot until republished.
              </p>
            </div>
          </div>

          {/* Status-specific messages */}
          {needsQA && (
            <div className="flex items-start gap-3 p-3 bg-amber-500/10 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-sm text-amber-600">QA Required</p>
                <p className="text-xs text-muted-foreground">
                  Run and pass QA tests before the bot can be reviewed and published.
                </p>
              </div>
            </div>
          )}

          {needsApproval && (
            <div className="flex items-start gap-3 p-3 bg-blue-500/10 rounded-lg">
              <Clock className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-sm text-blue-600">Awaiting Client Approval</p>
                <p className="text-xs text-muted-foreground">
                  Client needs to review and approve the bot before publishing.
                </p>
              </div>
            </div>
          )}

          {/* Publish Button */}
          <div className="flex justify-end">
            <Button
              onClick={() => setShowPublishDialog(true)}
              disabled={!canPublish || isLive}
              size="lg"
            >
              {isLive ? (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Already Live
                </>
              ) : (
                <>
                  <Rocket className="w-4 h-4 mr-2" />
                  Publish to Production
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Client Approval Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Client Approval Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : approval ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm">Status</span>
                <Badge 
                  variant={approval.status === 'approved' ? 'default' : 'secondary'}
                  className={approval.status === 'approved' ? 'bg-green-500/10 text-green-600' : ''}
                >
                  {approval.status === 'approved' ? (
                    <>
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Approved
                    </>
                  ) : approval.status === 'rejected' ? (
                    <>
                      <XCircle className="w-3 h-3 mr-1" />
                      Changes Requested
                    </>
                  ) : (
                    <>
                      <Clock className="w-3 h-3 mr-1" />
                      Pending
                    </>
                  )}
                </Badge>
              </div>
              {approval.feedback_text && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Feedback</p>
                  <p className="text-sm bg-muted p-2 rounded">{approval.feedback_text}</p>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Last updated: {new Date(approval.created_at).toLocaleString()}
              </p>
            </div>
          ) : (
            <div className="text-center py-4">
              <Clock className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                No approval request sent yet. Bot must pass QA first.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

       {/* Client Preview Link Card */}
       <Card>
         <CardHeader>
           <CardTitle className="text-base flex items-center gap-2">
             <Link2 className="w-4 h-4" />
             Client Preview Link
           </CardTitle>
           <CardDescription>
             Generate a shareable link for clients to preview and approve the assistant
           </CardDescription>
         </CardHeader>
         <CardContent className="space-y-4">
           {/* Standard preview link (requires login) */}
           <div className="space-y-2">
             <Label className="text-sm text-muted-foreground">Standard Link (requires login)</Label>
             <div className="flex items-center gap-2">
               <div className="flex-1 p-2 bg-muted rounded font-mono text-xs truncate">
                 {previewUrl}
               </div>
               <Button 
                 variant="outline" 
                 size="sm"
                 onClick={() => handleCopyLink(previewUrl)}
               >
                 {linkCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
               </Button>
               <Button 
                 variant="outline" 
                 size="sm"
                 onClick={() => window.open(`/preview/${botId}`, '_blank')}
               >
                 <ExternalLink className="w-4 h-4" />
               </Button>
             </div>
           </div>
 
           <Separator />
 
           {/* Magic link generator */}
           <div className="space-y-3">
             <Label className="text-sm font-medium">Magic Link (no login required)</Label>
             <p className="text-xs text-muted-foreground">
               Generate a one-time link that expires in 7 days. Perfect for sharing with clients who don't have an account.
             </p>
             
             <div className="flex items-center gap-2">
               <Input
                 type="email"
                 placeholder="Client email (optional)"
                 value={recipientEmail}
                 onChange={(e) => setRecipientEmail(e.target.value)}
                 className="flex-1"
               />
               <Button 
                 onClick={generateMagicLink}
                 disabled={generatingLink}
                 size="sm"
               >
                 {generatingLink ? (
                   <Clock className="w-4 h-4 mr-2 animate-spin" />
                 ) : (
                   <Mail className="w-4 h-4 mr-2" />
                 )}
                 Generate Link
               </Button>
             </div>
 
             {magicLink && (
               <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/20 space-y-2">
                 <div className="flex items-center gap-2">
                   <CheckCircle className="w-4 h-4 text-green-600" />
                   <span className="text-sm font-medium text-green-600">Magic link ready!</span>
                 </div>
                 <div className="flex items-center gap-2">
                   <div className="flex-1 p-2 bg-background rounded font-mono text-xs truncate border">
                     {magicLink.url}
                   </div>
                   <Button 
                     variant="outline" 
                     size="sm"
                     onClick={() => handleCopyLink(magicLink.url)}
                   >
                     <Copy className="w-4 h-4" />
                   </Button>
                 </div>
                 <p className="text-xs text-muted-foreground">
                   Expires: {new Date(magicLink.expiresAt).toLocaleDateString()} at {new Date(magicLink.expiresAt).toLocaleTimeString()}
                 </p>
               </div>
             )}
           </div>
         </CardContent>
       </Card>
 
      {/* Publish Confirmation Dialog */}
      <Dialog open={showPublishDialog} onOpenChange={setShowPublishDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Publish Bot to Production?</DialogTitle>
            <DialogDescription>
              This will make the bot live and available to visitors. The governance pack version will be locked.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-4">
            <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
              <Lock className="w-5 h-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="font-medium text-sm">Version Lock</p>
                <p className="text-xs text-muted-foreground">
                  Governance pack v{governanceVersion || 1} will be locked to this bot.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-green-500/10 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-sm text-green-600">Ready to Deploy</p>
                <p className="text-xs text-muted-foreground">
                  All pre-publish checks have passed.
                </p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPublishDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handlePublish} disabled={publishing}>
              {publishing ? (
                <>Publishing...</>
              ) : (
                <>
                  <Rocket className="w-4 h-4 mr-2" />
                  Publish Now
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BotPublishing;
